import React from 'react';
import OrderQuantityPreview, { getOrderQuantityPreviewEquivalents } from '../components/OrderQuantityPreview';
import { normalizeUnitCode } from '../../../utils/units';
import { Button, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderDto, SupplierOrderItemDto } from '../../../dto/supplierOrder';
import type { SupplierOrderTransportDto } from '../../../dto/transport';
import { formatCurrency } from '../../../utils/currency';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { buildSupplierOrderCostSummary, type SupplierOrderCostRow } from './supplierOrderCost';

interface SupplierOrderCostCardProps {
  supplierOrder: SupplierOrderDto;
  /** Trucks of this ZD (null = not loaded; the freight then reads „nieznany”). */
  transports: SupplierOrderTransportDto[] | null;
  /** The purchase invoice has moved the cost from the forecast to the company result. */
  inResult: boolean;
  /** „Edytuj pozycje” — opens the items dialog (viewers who manage ZD). */
  onEditItems?: () => void;
  /** „Dopisz koszt odprawy” — opens the landed costs form (viewers who edit ZD finance). */
  onEditCustoms?: () => void;
}

const money = (value: number | null): string => (value === null ? '—' : formatCurrency(value, 'PLN'));

/**
 * „Koszt dostawy (partia)” of the ZD card (makieta `renderBelow`): per item the
 * value of the goods and its share of the freight, then goods / freight and
 * customs / total. Figures come from the order and its trucks as the backend
 * returns them (`totalCostPLN`, truck `freight`), nothing is recomputed from
 * prices on the page. Only for viewers who may see purchase prices.
 */
export default function SupplierOrderCostCard({ supplierOrder, transports, inResult, onEditItems, onEditCustoms }: SupplierOrderCostCardProps) {
  const { t } = useTranslation(['orders']);
  const summary = buildSupplierOrderCostSummary(supplierOrder, transports);
  const key = (name: string) => t(`orders:supplierOrders.card.cost.${name}`);

  const columns: ColumnsType<SupplierOrderCostRow> = [
    {
      title: key('item'),
      key: 'item',
      render: (_value, row) => {
        const item = supplierOrder.items?.find((candidate: SupplierOrderItemDto) => candidate.id === row.id);
        const dimensions = item ? formatProductDimensions(item) : null;
        const pricingQuantityShown = item && getOrderQuantityPreviewEquivalents(item).some(
          ({ code, value }) => code === normalizeUnitCode(item.purchaseUnit) && value === item.purchaseQuantity,
        );
        return (
          <div>
            <b>{row.name}</b>
            {dimensions ? <div className="zd-cost__sub">{dimensions}</div> : null}
            <div className="zd-cost__sub">
              {item ? <OrderQuantityPreview line={item} /> : row.quantityLabel}
              {row.unitCostLabel ? ` · ${row.unitCostLabel}` : ''}
              {row.pricingQuantityLabel && !pricingQuantityShown ? ` × ${row.pricingQuantityLabel}` : ''}
            </div>
          </div>
        );
      },
    },
    { title: key('goods'), key: 'goods', align: 'right', render: (_value, row) => money(row.goodsPLN) },
    { title: key('freight'), key: 'freight', align: 'right', render: (_value, row) => (row.freightPLN === null ? '—' : money(row.freightPLN)) },
    {
      title: key('total'),
      key: 'total',
      align: 'right',
      render: (_value, row) => (
        <div>
          <b>{money(row.totalPLN)}</b>
          {row.perUnitPLN !== null ? (
            <div className="zd-cost__sub">{t('orders:supplierOrders.card.cost.perUnit', { amount: money(row.perUnitPLN), unit: row.unitLabel })}</div>
          ) : null}
          <Tag color={row.goodsPLN !== null && row.freightPLN !== null ? 'green' : 'gold'} style={{ marginInlineEnd: 0 }}>
            {row.goodsPLN !== null && row.freightPLN !== null ? key('complete') : key('incomplete')}
          </Tag>
        </div>
      ),
    },
  ];

  return (
    <section className="zk-section" data-testid="supplier-order-cost-card">
      <div className="zk-section__header">
        <h3 className="zk-section__title">{key('title')}</h3>
        <span className="zk-section__aside">
          {onEditItems ? <Button size="small" onClick={onEditItems} data-testid="supplier-order-cost-edit-items">{key('editItems')}</Button> : null}
          {onEditCustoms ? <Button size="small" onClick={onEditCustoms} data-testid="supplier-order-cost-edit-customs">{key('editCustoms')}</Button> : null}
          <Tag color={inResult ? 'green' : 'blue'}>{inResult ? key('result') : key('forecast')}</Tag>
        </span>
      </div>
      <Table<SupplierOrderCostRow>
        size="small"
        rowKey="id"
        pagination={false}
        columns={columns}
        dataSource={summary.rows}
        scroll={{ x: 560 }}
      />
      <div className="zd-cost__sums" data-testid="supplier-order-cost-summary">
        <div><span>{key('goods')}</span><b>{money(summary.goodsPLN)}</b></div>
        <div>
          <span>{key('freight')}</span>
          <b>{summary.freightPLN === null ? key('freightUnknown') : money(summary.freightPLN)}</b>
        </div>
        <div><span>{key('customs')}</span><b>{money(summary.customsPLN)}</b></div>
        <div className="zd-cost__sum-total"><span>{key('total')}</span><b>{summary.totalPLN === null ? '—' : money(summary.totalPLN)}</b></div>
      </div>
      {summary.incompletePrices ? <p className="zd-cost__note">{key('noPrices')}</p> : null}
      <p className="zd-cost__note">{key('note')}</p>
    </section>
  );
}
